import { ref } from "vue";

import type { ConsentTypes } from "@solvari/utils";

import { createGtmEvent } from "@solvari/argus";
import { getCookie, setCookie, singleton } from "@solvari/utils";

const consentTypes = [
  "functionality_storage",
  "analytics_storage",
  "ad_storage",
] as const satisfies ConsentTypes;

const alwaysGrantedConsentTypes: ConsentTypes = ["functionality_storage"];

type ConsentState = Record<ConsentTypes[number], "denied" | "granted">;

const cookiePopupState = singleton(() => {
  const isClient = !(import.meta as { server?: boolean }).server;
  const consentCookie = isClient ? getCookie("solvariCookieConsent") : null;
  const visible = ref(isClient && !consentCookie);

  return { visible, consentCookie };
});

function useCookiePopup() {
  function sendConsentUpdateEvent(cookieConsentCookie: ConsentTypes) {
    const acceptedTypes = [
      ...new Set([...cookieConsentCookie, ...alwaysGrantedConsentTypes]),
    ];

    const consentState = consentTypes.reduce((consentState, consentType) => {
      const granted = acceptedTypes.includes(consentType);

      consentState[consentType] = granted ? "granted" : "denied";

      return consentState;
    }, {} as ConsentState);

    void createGtmEvent("consent_update", consentState);
  }

  function acceptAll() {
    acceptCookies(consentTypes);
  }

  function acceptCookies(acceptedCookies: ConsentTypes) {
    const date = new Date();
    date.setFullYear(date.getFullYear() + 1);

    const acceptedTypes = [
      ...new Set([...acceptedCookies, ...alwaysGrantedConsentTypes]),
    ];

    setCookie({
      name: "solvariCookieConsent",
      value: acceptedTypes,
      expires: date.toUTCString(),
    });
    sendConsentUpdateEvent(acceptedTypes);
  }

  const { visible, consentCookie } = cookiePopupState();

  if (consentCookie) {
    sendConsentUpdateEvent(consentCookie);
  }

  const step = ref(1);

  const analyticsStorage = ref(
    consentCookie?.includes("analytics_storage") ?? false,
  );
  const adStorage = ref(consentCookie?.includes("ad_storage") ?? false);

  function reset() {
    step.value = 1;
    visible.value = false;
  }

  function acceptClick() {
    acceptAll();
    reset();
  }

  function submitCustom() {
    const selectedSettings: ConsentTypes[number][] = ["functionality_storage"];
    if (analyticsStorage.value) {
      selectedSettings.push("analytics_storage");
    }
    if (adStorage.value) {
      selectedSettings.push("ad_storage");
    }

    acceptCookies(selectedSettings);
    reset();
  }

  return {
    visible,
    step,
    analyticsStorage,
    adStorage,
    acceptClick,
    submitCustom,
  };
}

export { cookiePopupState, useCookiePopup };
export type { ConsentTypes };
